/**
 * AURELIA — component-recommendations.js
 *
 * <product-recommendations>
 *   Fetches its own section HTML from the Product Recommendations API and
 *   replaces its skeleton with the result.
 *
 * Design notes
 * ------------
 * The fetch is deferred until the section is near the viewport. On a product
 * page the recommendations row is almost always below the fold, and a request
 * fired at parse time competes for bandwidth with the product images that are
 * the LCP. An IntersectionObserver with a generous margin means the row is
 * ready by the time it is scrolled to, without ever racing the fold.
 *
 * The server returns fully rendered Liquid, so cards, badges, swatches,
 * quick-add and prices are all produced by the same code as everywhere else.
 * There is no client-side product templating anywhere in this theme.
 */
(function () {
  'use strict';

  const { Element, fetchSection, a11y } = window.Aurelia;

  class ProductRecommendations extends Element {
    mount() {
      // Guard: without a product id there is nothing to be similar to. This
      // happens in the Theme Editor when the section is dragged onto a
      // template that has no product context.
      if (!this.dataset.productId) return;

      this.loaded = false;

      // `rootMargin` is one viewport: fetch when the row is a screen away.
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.load();
        },
        { rootMargin: '600px 0px' }
      );

      this.observer.observe(this);
    }

    unmount() {
      if (this.observer) this.observer.disconnect();
      if (this.controller) this.controller.abort();
    }

    async load() {
      if (this.loaded) return;
      this.loaded = true;
      this.observer.disconnect();

      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('product_id', this.dataset.productId);
      url.searchParams.set('limit', this.dataset.limit || '4');
      url.searchParams.set('intent', this.dataset.intent || 'related');

      this.controller = new AbortController();

      try {
        const html = await fetchSection(url.toString(), {
          sectionId: this.dataset.sectionId,
          signal: this.controller.signal,
        });

        const incoming = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('product-recommendations');

        // No recommendations for this product: remove the whole section rather
        // than leaving a heading over an empty row. Shopify returns an empty
        // body in that case, so `incoming` may legitimately be null.
        if (!incoming || incoming.children.length === 0) {
          this.closest('.shopify-section')?.remove();
          return;
        }

        this.innerHTML = incoming.innerHTML;
        this.dataset.loaded = 'true';

        const heading = this.querySelector('.section-header__heading');
        if (heading) a11y.announce(heading.textContent.trim());
      } catch (error) {
        if (error.name === 'AbortError') return;
        // A failed recommendation fetch is not worth showing a shopper an
        // error for — the section simply does not appear.
        console.warn('[Aurelia] recommendations failed to load', error);
        this.closest('.shopify-section')?.remove();
      }
    }
  }

  window.Aurelia.define('product-recommendations', ProductRecommendations);
})();
